/**
 * DataSourceManager - Centralized data source priority and fallback handling
 * 
 * This service manages the priority order of data sources and implements
 * intelligent fallback mechanisms to ensure data availability.
 * 
 * Data Source Priority (highest to lowest):
 * 1. Airtable (most accurate, real-time)
 * 2. D1 Database (cached, reliable)
 * 3. Mock Data (development/testing)
 */

import { log } from './LoggingService';

export enum DataSourceType {
  AIRTABLE = 'airtable',
  D1_DATABASE = 'd1_database',
  MOCK_DATA = 'mock_data',
  CACHE = 'cache'
}

export enum DataSourceStatus {
  AVAILABLE = 'available',
  DEGRADED = 'degraded',
  UNAVAILABLE = 'unavailable',
  ERROR = 'error'
}

export interface DataSourceConfig {
  type: DataSourceType;
  priority: number;
  timeout: number;
  retryAttempts: number;
  fallbackToNext: boolean;
  healthCheckEndpoint?: string;
}

export interface DataSourceResult<T = any> {
  data: T;
  source: DataSourceType;
  status: DataSourceStatus;
  responseTime: number;
  timestamp: Date;
  metadata?: {
    cacheHit?: boolean;
    staleData?: boolean;
    errorMessage?: string;
    retryCount?: number;
  };
}

export interface DataSourceHealth {
  source: DataSourceType;
  status: DataSourceStatus;
  lastChecked: Date;
  responseTime?: number;
  errorMessage?: string;
  uptime: number;
}

export class DataSourceManager {
  private static instance: DataSourceManager;
  private dataSources: Map<DataSourceType, DataSourceConfig> = new Map();
  private healthStatus: Map<DataSourceType, DataSourceHealth> = new Map();
  private baseUrl: string;
  // Request deduplication: cache in-flight requests to prevent duplicates
  private inFlightRequests: Map<string, Promise<any>> = new Map();

  private constructor() {
    // Use environment variable for API endpoint
    this.baseUrl = import.meta.env.VITE_API_ENDPOINT || 'https://spog-aggregation.mike-532.workers.dev';
    const isDevelopment = import.meta.env.DEV;

    // Only log in development to avoid production console spam
    if (isDevelopment) {
      console.log('🔧 DataSourceManager using URL:', this.baseUrl);
    }
    this.initializeDataSources();
  }

  public static getInstance(): DataSourceManager {
    if (!DataSourceManager.instance) {
      DataSourceManager.instance = new DataSourceManager();
    }
    return DataSourceManager.instance;
  }

  /**
   * Initialize data source configurations
   */
  private initializeDataSources(): void {
    // Airtable - Primary source (most accurate)
    this.dataSources.set(DataSourceType.AIRTABLE, {
      type: DataSourceType.AIRTABLE,
      priority: 1,
      timeout: 5000, // 5 seconds
      retryAttempts: 2,
      fallbackToNext: true,
      healthCheckEndpoint: '/api/health/airtable'
    });

    // D1 Database - Secondary source (cached, reliable)
    this.dataSources.set(DataSourceType.D1_DATABASE, {
      type: DataSourceType.D1_DATABASE,
      priority: 2,
      timeout: 3000, // 3 seconds
      retryAttempts: 1,
      fallbackToNext: true,
      healthCheckEndpoint: '/api/health/d1'
    });

    // Mock Data - Fallback source (development/testing)
    this.dataSources.set(DataSourceType.MOCK_DATA, {
      type: DataSourceType.MOCK_DATA,
      priority: 3,
      timeout: 1000, // 1 second
      retryAttempts: 0,
      fallbackToNext: false
    });

    // Cache - Fastest source (if available)
    this.dataSources.set(DataSourceType.CACHE, {
      type: DataSourceType.CACHE,
      priority: 0, // Highest priority when available
      timeout: 500, // 0.5 seconds
      retryAttempts: 0,
      fallbackToNext: true
    });
  }

  /**
   * Get data from the best available source
   */
  async getData<T = any>(
    endpoint: string,
    params: Record<string, any> = {},
    options: {
      forceRefresh?: boolean;
      preferredSource?: DataSourceType;
      timeout?: number;
    } = {}
  ): Promise<DataSourceResult<T>> {
    const startTime = Date.now();
    const { forceRefresh = false, preferredSource, timeout } = options;

    // REQUEST DEDUPLICATION: Create unique key for this request
    const requestKey = `${endpoint}:${JSON.stringify(params)}:${forceRefresh}`;

    // If this exact request is already in flight, return the existing promise
    if (this.inFlightRequests.has(requestKey)) {
      log.debug(`⚡ Deduplicating request: ${endpoint}`, 'DataSourceManager');
      return this.inFlightRequests.get(requestKey)!;
    }

    // Create new request promise with deduplication cleanup
    const requestPromise = (async () => {
      try {
        // Check cache first if not forcing refresh
        if (!forceRefresh) {
          const cacheResult = await this.tryDataSource(DataSourceType.CACHE, endpoint, params, timeout);
          if (cacheResult.status === DataSourceStatus.AVAILABLE) {
            return cacheResult;
          }
        }

        // Get sources in priority order
        const sources = this.getSourcesInPriorityOrder(preferredSource);

        for (const source of sources) {
          try {
            const result = await this.tryDataSource(source, endpoint, params, timeout);

            if (result.status === DataSourceStatus.AVAILABLE) {
              // Update health status
              this.updateHealthStatus(source, DataSourceStatus.AVAILABLE, Date.now() - startTime);
              return result;
            }

            // If source is degraded but has data, use it
            if (result.status === DataSourceStatus.DEGRADED && result.data) {
              log.warn(`Using degraded data from ${source}`, 'DataSourceManager');
              this.updateHealthStatus(source, DataSourceStatus.DEGRADED, Date.now() - startTime);
              return result;
            }

            // If source failed and fallback is enabled, try next source
            const config = this.dataSources.get(source);
            if (config?.fallbackToNext) {
              log.warn(`Source ${source} failed, trying next source`, 'DataSourceManager');
              continue;
            }

          } catch (error) {
            log.error(`Error with source ${source}:`, 'DataSourceManager', error);
            this.updateHealthStatus(source, DataSourceStatus.ERROR, undefined, error.message);

            // Continue to next source if fallback is enabled
            const config = this.dataSources.get(source);
            if (config?.fallbackToNext) {
              continue;
            }
          }
        }

        // All sources failed
        throw new Error('All data sources failed. Unable to fetch data.');
      } finally {
        // Remove from in-flight map when complete (success or failure)
        this.inFlightRequests.delete(requestKey);
      }
    })();

    // Store in-flight request for deduplication
    this.inFlightRequests.set(requestKey, requestPromise);

    return requestPromise;
  }

  /**
   * Try a specific data source
   */
  private async tryDataSource<T = any>(
    source: DataSourceType,
    endpoint: string,
    params: Record<string, any>,
    customTimeout?: number
  ): Promise<DataSourceResult<T>> {
    const startTime = Date.now();
    const config = this.dataSources.get(source);
    
    if (!config) {
      throw new Error(`Unknown data source: ${source}`);
    }

    const timeout = customTimeout || config.timeout;

    try {
      let data: T;
      let status = DataSourceStatus.AVAILABLE;

      switch (source) {
        case DataSourceType.CACHE:
          data = await this.getFromCache<T>(endpoint, params);
          break;
        
        case DataSourceType.AIRTABLE:
          data = await this.getFromAirtable<T>(endpoint, params, timeout);
          break;
        
        case DataSourceType.D1_DATABASE:
          data = await this.getFromD1<T>(endpoint, params, timeout);
          break;
        
        case DataSourceType.MOCK_DATA:
          data = await this.getFromMock<T>(endpoint, params);
          break;
        
        default:
          throw new Error(`Unsupported data source: ${source}`);
      }

      const responseTime = Date.now() - startTime;
      
      return {
        data,
        source,
        status,
        responseTime,
        timestamp: new Date(),
        metadata: {
          cacheHit: source === DataSourceType.CACHE
        }
      };

    } catch (error) {
      const responseTime = Date.now() - startTime;
      
      return {
        data: null as T,
        source,
        status: DataSourceStatus.ERROR,
        responseTime,
        timestamp: new Date(),
        metadata: {
          errorMessage: error.message,
          retryCount: 0
        }
      };
    }
  }

  /**
   * Get data from cache
   */
  private async getFromCache<T>(endpoint: string, params: Record<string, any>): Promise<T> {
    // This would integrate with the existing cache system
    // For now, return null to indicate cache miss
    throw new Error('Cache miss');
  }

  /**
   * Get data from Airtable
   */
  private async getFromAirtable<T>(endpoint: string, params: Record<string, any>, timeout: number): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;

    // Build full URL - handle endpoints that already contain query parameters
    let fullUrl = url;
    if (params && Object.keys(params).length > 0) {
      const searchParams = new URLSearchParams(params);
      const separator = endpoint.includes('?') ? '&' : '?';
      fullUrl = `${url}${separator}${searchParams}`;
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      const response = await fetch(fullUrl, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`Airtable API error: ${response.status} ${response.statusText}`);
      }

      return await response.json();

    } catch (error) {
      clearTimeout(timeoutId);
      throw error;
    }
  }

  /**
   * Get data from D1 Database
   */
  private async getFromD1<T>(endpoint: string, params: Record<string, any>, timeout: number): Promise<T> {
    // D1 database queries would go here
    // For now, delegate to the main API
    return this.getFromAirtable<T>(endpoint, params, timeout);
  }

  /**
   * Get mock data
   */
  private async getFromMock<T>(endpoint: string, params: Record<string, any>): Promise<T> {
    // Generate mock data based on endpoint and params
    // This would integrate with existing mock data generators
    throw new Error('Mock data not implemented');
  }

  /**
   * Get sources in priority order
   */
  private getSourcesInPriorityOrder(preferredSource?: DataSourceType): DataSourceType[] {
    const sources = Array.from(this.dataSources.keys());
    
    // Sort by priority
    sources.sort((a, b) => {
      const configA = this.dataSources.get(a);
      const configB = this.dataSources.get(b);
      return (configA?.priority || 999) - (configB?.priority || 999);
    });

    // Move preferred source to front if specified
    if (preferredSource && sources.includes(preferredSource)) {
      const index = sources.indexOf(preferredSource);
      sources.splice(index, 1);
      sources.unshift(preferredSource);
    }

    return sources;
  }

  /**
   * Update health status for a data source
   */
  private updateHealthStatus(
    source: DataSourceType,
    status: DataSourceStatus,
    responseTime?: number,
    errorMessage?: string
  ): void {
    const existing = this.healthStatus.get(source);
    const now = new Date();
    
    this.healthStatus.set(source, {
      source,
      status,
      lastChecked: now,
      responseTime,
      errorMessage,
      uptime: existing ? now.getTime() - existing.lastChecked.getTime() : 0
    });
  }

  /**
   * Get health status of all data sources
   */
  getHealthStatus(): DataSourceHealth[] {
    return Array.from(this.healthStatus.values());
  }

  /**
   * Check health of a specific data source
   */
  async checkHealth(source: DataSourceType): Promise<DataSourceHealth> {
    const config = this.dataSources.get(source);
    if (!config) {
      throw new Error(`Unknown data source: ${source}`);
    }

    const startTime = Date.now();
    let status = DataSourceStatus.AVAILABLE;
    let errorMessage: string | undefined;

    try {
      if (config.healthCheckEndpoint) {
        const response = await fetch(`${this.baseUrl}${config.healthCheckEndpoint}`, {
          method: 'GET',
          signal: AbortSignal.timeout(config.timeout)
        });

        if (!response.ok) {
          status = DataSourceStatus.ERROR;
          errorMessage = `Health check failed: ${response.status}`;
        }
      }
    } catch (error) {
      status = DataSourceStatus.ERROR;
      errorMessage = error.message;
    }

    const responseTime = Date.now() - startTime;
    const health: DataSourceHealth = {
      source,
      status,
      lastChecked: new Date(),
      responseTime,
      errorMessage,
      uptime: 0
    };

    this.healthStatus.set(source, health);
    return health;
  }

  /**
   * Get the best available data source
   */
  getBestAvailableSource(): DataSourceType | null {
    const sources = this.getSourcesInPriorityOrder();
    
    for (const source of sources) {
      const health = this.healthStatus.get(source);
      if (health && health.status === DataSourceStatus.AVAILABLE) {
        return source;
      }
    }

    return null;
  }

  /**
   * Force refresh all caches
   */
  async refreshAllCaches(): Promise<void> {
    // This would trigger cache refresh across all sources
    log.info('Refreshing all caches', 'DataSourceManager');
    
    // Implementation would depend on the cache system
    // For now, just log the action
  }
}

// Export singleton instance
export const dataSourceManager = DataSourceManager.getInstance();
